import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'
import { v4 as uuidv4 } from 'uuid'
import { extractTextFromPdf } from '../utils/pdfExtract'
import { aiService } from '../services/ai'

const Page = styled.div`min-height:100%;background:#0f0f0f;padding:2rem 2rem 3rem;max-width:860px;margin:0 auto`
const Header = styled.div`margin-bottom:2rem`
const Section = styled.div`background:#141414;border:1px solid #2a2a2a;border-radius:10px;padding:1.5rem;margin-bottom:1.5rem`
const SectionTitle = styled.h3`font-size:1rem;font-weight:700;color:#f2f2f2;margin:0 0 1.25rem;padding-bottom:.75rem;border-bottom:1px solid #2a2a2a`
const Grid2 = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:1rem;@media(max-width:600px){grid-template-columns:1fr}`
const FieldGroup = styled.div`display:flex;flex-direction:column;gap:.3rem`
const Label = styled.label`font-size:.75rem;font-weight:600;color:#7f7f7f;text-transform:uppercase;letter-spacing:.04em`
const Input = styled.input`padding:.55rem .75rem;border:1px solid #3a3a3a;border-radius:6px;font-size:.9rem;color:#f2f2f2;background:#1a1a1a;outline:none;transition:border-color .15s;&:focus{border-color:#8888e8;box-shadow:0 0 0 2px rgba(136,136,232,.2)}`
const TextArea = styled.textarea`padding:.55rem .75rem;border:1px solid #3a3a3a;border-radius:6px;font-size:.88rem;color:#f2f2f2;background:#1a1a1a;outline:none;resize:vertical;line-height:1.5;min-height:80px;transition:border-color .15s;&:focus{border-color:#8888e8;box-shadow:0 0 0 2px rgba(136,136,232,.2)}`
const EntryCard = styled.div`border:1px solid #2a2a2a;border-radius:8px;padding:1.1rem;margin-bottom:1rem;background:#1a1a1a`
const EntryHeader = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem`
const RemoveBtn = styled.button`font-size:.75rem;color:#ff8888;background:none;border:none;cursor:pointer;padding:0;&:hover{text-decoration:underline}`
const AddBtn = styled.button`font-size:.85rem;font-weight:600;color:#8888e8;background:none;border:1px dashed #3a3a6a;border-radius:6px;padding:.5rem 1rem;cursor:pointer;width:100%;margin-top:.5rem;transition:all .15s;&:hover{background:#0d0d1a;border-color:#8888e8}`
const SaveBtn = styled.button`background:#1a202c;color:white;border:none;border-radius:7px;padding:.75rem 2.5rem;font-size:.95rem;font-weight:600;cursor:pointer;transition:background .15s;&:hover{background:#2d3748}`
const SaveRow = styled.div`display:flex;justify-content:flex-end;align-items:center;gap:1rem;margin-top:1rem`
const CheckboxRow = styled.label`display:flex;align-items:center;gap:.4rem;font-size:.85rem;color:#7f7f7f;cursor:pointer`

export const ProfileForm = ({ profile, onSave, onAutoSave }) => {
  const [form, setForm] = useState(profile)
  const [saved, setSaved] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState(null)
  const [importSuccess, setImportSuccess] = useState(false)
  const fileInputRef = useRef(null)

  const set = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value }
      onAutoSave?.(next)
      return next
    })
    setSaved(false)
  }

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setImporting(true); setImportError(null); setImportSuccess(false)
    try {
      const text = await extractTextFromPdf(file)
      const parsed = await aiService.parseProfileFromResume(text)
      setForm(prev => {
        const next = {
          ...prev,
          name:       parsed.name       || prev.name,
          email:      parsed.email      || prev.email,
          phone:      parsed.phone      || prev.phone,
          location:   parsed.location   || prev.location,
          linkedin:   parsed.linkedin   || prev.linkedin,
          github:     parsed.github     || prev.github,
          experience: parsed.experience?.length
            ? parsed.experience.map(e => ({ id: uuidv4(), company: e.company || '', title: e.title || '', startDate: e.startDate || '', endDate: e.endDate || '', current: !!e.current, bullets: e.bullets?.length ? e.bullets : [''] }))
            : prev.experience,
          education: parsed.education?.length
            ? parsed.education.map(e => ({ id: uuidv4(), school: e.school || '', degree: e.degree || '', field: e.field || '', gpa: e.gpa || '', startDate: e.startDate || '', endDate: e.endDate || '' }))
            : prev.education,
          skills: parsed.skills?.length ? parsed.skills : prev.skills,
        }
        onAutoSave?.(next)
        return next
      })
      setImportSuccess(true)
      setSaved(false)
    } catch (err) {
      setImportError(err.message || 'Import failed. Try filling in manually.')
    } finally { setImporting(false) }
  }

  const addExp = () => set('experience', [...form.experience, { id: uuidv4(), company: '', title: '', startDate: '', endDate: '', current: false, bullets: [''] }])
  const updateExp = (id, field, value) => set('experience', form.experience.map(e => e.id === id ? { ...e, [field]: value } : e))
  const removeExp = (id) => set('experience', form.experience.filter(e => e.id !== id))
  const updateBullet = (expId, idx, value) => set('experience', form.experience.map(e => { if (e.id !== expId) return e; const b = [...e.bullets]; b[idx] = value; return { ...e, bullets: b } }))
  const addBullet = (expId) => set('experience', form.experience.map(e => e.id === expId ? { ...e, bullets: [...e.bullets, ''] } : e))
  const removeBullet = (expId, idx) => set('experience', form.experience.map(e => e.id !== expId ? e : { ...e, bullets: e.bullets.filter((_, i) => i !== idx) }))
  const addEdu = () => set('education', [...form.education, { id: uuidv4(), school: '', degree: '', field: '', startDate: '', endDate: '' }])
  const updateEdu = (id, field, value) => set('education', form.education.map(e => e.id === id ? { ...e, [field]: value } : e))
  const removeEdu = (id) => set('education', form.education.filter(e => e.id !== id))

  return (
    <Page>
      <Header>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a202c', margin: '0 0 .25rem' }}>Your Profile</h2>
        <p style={{ fontSize: '.875rem', color: '#718096', margin: 0 }}>Enter your information once — ark uses it to tailor every resume.</p>
      </Header>

      {/* ── PDF Import ── */}
      <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: '#f7faff', border: '1px solid #bee3f8', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, color: '#1a202c', fontSize: '.9rem', marginBottom: '.2rem' }}>Import from PDF</div>
            <div style={{ fontSize: '.8rem', color: '#718096' }}>Upload your existing resume — ark extracts and pre-fills everything.</div>
          </div>
          <input type="file" accept=".pdf" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePdfImport} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            style={{ padding: '.5rem 1.2rem', background: importing ? '#e2e8f0' : '#ebf8ff', border: '1px solid #90cdf4', borderRadius: '6px', fontSize: '.85rem', color: importing ? '#a0aec0' : '#2b6cb0', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {importing ? 'Reading PDF…' : 'Upload PDF'}
          </button>
        </div>
        {importError   && <div style={{ marginTop: '.65rem', fontSize: '.82rem', color: '#c53030' }}>{importError}</div>}
        {importSuccess && <div style={{ marginTop: '.65rem', fontSize: '.82rem', color: '#276749', fontWeight: 500 }}>✓ Profile imported — review the fields below and hit Save Profile.</div>}
      </div>

      <Section>
        <SectionTitle>Contact Information</SectionTitle>
        <Grid2>
          <FieldGroup><Label>Full Name *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" /></FieldGroup>
          <FieldGroup><Label>Email *</Label><Input value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" /></FieldGroup>
          <FieldGroup><Label>Phone</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" /></FieldGroup>
          <FieldGroup><Label>Location</Label><Input value={form.location} onChange={e => set('location', e.target.value)} placeholder="San Francisco, CA" /></FieldGroup>
          <FieldGroup><Label>LinkedIn</Label><Input value={form.linkedin || ''} onChange={e => set('linkedin', e.target.value)} placeholder="linkedin.com/in/janesmith" /></FieldGroup>
          <FieldGroup><Label>GitHub / Portfolio</Label><Input value={form.github || ''} onChange={e => set('github', e.target.value)} placeholder="github.com/janesmith" /></FieldGroup>
        </Grid2>
      </Section>

      <Section>
        <SectionTitle>Work Experience</SectionTitle>
        {form.experience.map((exp, i) => (
          <EntryCard key={exp.id}>
            <EntryHeader><span style={{ fontSize: '.82rem', fontWeight: 600, color: '#4a5568' }}>Position {i + 1}</span><RemoveBtn onClick={() => removeExp(exp.id)}>Remove</RemoveBtn></EntryHeader>
            <Grid2>
              <FieldGroup><Label>Company *</Label><Input value={exp.company} onChange={e => updateExp(exp.id, 'company', e.target.value)} placeholder="Acme Corp" /></FieldGroup>
              <FieldGroup><Label>Title *</Label><Input value={exp.title} onChange={e => updateExp(exp.id, 'title', e.target.value)} placeholder="Software Engineer" /></FieldGroup>
              <FieldGroup><Label>Start Date</Label><Input value={exp.startDate} onChange={e => updateExp(exp.id, 'startDate', e.target.value)} placeholder="Jan 2022" /></FieldGroup>
              <FieldGroup>
                <Label>End Date</Label>
                <Input value={exp.endDate} onChange={e => updateExp(exp.id, 'endDate', e.target.value)} placeholder="Dec 2024" disabled={exp.current} />
                <CheckboxRow><input type="checkbox" checked={exp.current} onChange={e => updateExp(exp.id, 'current', e.target.checked)} /> Current role</CheckboxRow>
              </FieldGroup>
            </Grid2>
            <FieldGroup style={{ marginTop: '.85rem' }}>
              <Label>Bullet Points <span style={{ fontSize: '.75rem', color: '#a0aec0', textTransform: 'none', fontWeight: 400 }}>— what you did, built, improved</span></Label>
              {exp.bullets.map((b, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-start' }}>
                  <TextArea value={b} onChange={e => updateBullet(exp.id, idx, e.target.value)} placeholder="Increased API response time by 40%..." style={{ minHeight: '52px', flex: 1 }} />
                  {exp.bullets.length > 1 && <RemoveBtn style={{ marginTop: '.4rem' }} onClick={() => removeBullet(exp.id, idx)}>✕</RemoveBtn>}
                </div>
              ))}
              <AddBtn onClick={() => addBullet(exp.id)} style={{ marginTop: '.25rem' }}>+ Add bullet</AddBtn>
            </FieldGroup>
          </EntryCard>
        ))}
        <AddBtn onClick={addExp}>+ Add position</AddBtn>
      </Section>

      <Section>
        <SectionTitle>Education</SectionTitle>
        {form.education.map((edu, i) => (
          <EntryCard key={edu.id}>
            <EntryHeader><span style={{ fontSize: '.82rem', fontWeight: 600, color: '#4a5568' }}>School {i + 1}</span><RemoveBtn onClick={() => removeEdu(edu.id)}>Remove</RemoveBtn></EntryHeader>
            <Grid2>
              <FieldGroup><Label>School *</Label><Input value={edu.school} onChange={e => updateEdu(edu.id, 'school', e.target.value)} placeholder="UC Berkeley" /></FieldGroup>
              <FieldGroup><Label>Degree</Label><Input value={edu.degree} onChange={e => updateEdu(edu.id, 'degree', e.target.value)} placeholder="Bachelor of Science" /></FieldGroup>
              <FieldGroup><Label>Field of Study</Label><Input value={edu.field} onChange={e => updateEdu(edu.id, 'field', e.target.value)} placeholder="Computer Science" /></FieldGroup>
              <FieldGroup><Label>GPA</Label><Input value={edu.gpa || ''} onChange={e => updateEdu(edu.id, 'gpa', e.target.value)} placeholder="3.8 (optional)" /></FieldGroup>
              <FieldGroup><Label>Start</Label><Input value={edu.startDate} onChange={e => updateEdu(edu.id, 'startDate', e.target.value)} placeholder="Aug 2018" /></FieldGroup>
              <FieldGroup><Label>End</Label><Input value={edu.endDate} onChange={e => updateEdu(edu.id, 'endDate', e.target.value)} placeholder="May 2022" /></FieldGroup>
            </Grid2>
          </EntryCard>
        ))}
        <AddBtn onClick={addEdu}>+ Add school</AddBtn>
      </Section>

      <Section>
        <SectionTitle>Skills</SectionTitle>
        <FieldGroup>
          <Label>Skills <span style={{ fontSize: '.75rem', color: '#a0aec0', textTransform: 'none', fontWeight: 400 }}>— comma separated</span></Label>
          <TextArea value={form.skills.join(', ')} onChange={e => set('skills', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Python, TypeScript, React, AWS, Docker" style={{ minHeight: '72px' }} />
        </FieldGroup>
      </Section>

      <SaveRow>
        {saved && <span style={{ fontSize: '.85rem', color: '#276749', fontWeight: 500 }}>✓ Profile saved</span>}
        <SaveBtn onClick={() => { onSave(form); setSaved(true) }}>Save Profile</SaveBtn>
      </SaveRow>
    </Page>
  )
}
